/**
 * Trip Feasibility - sliding panel with 3 columns.
 * Column 1: Trip cards + Check feasibility button
 * Column 2: Possible drivers per trip
 * Column 3: Driver detail (bucket, allocated trips)
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Zap, X, MapPin, CheckCircle, Clock, Trash2, Loader2, AlertCircle } from 'lucide-react';
import TempTripCard from './TempTripCard';
import PossibleDriversList from './PossibleDriversList';
import DriverDetailView from './DriverDetailView';
import { checkFeasibility } from '../../utils/tempTripFeasibility';
import { haversineDistance } from '../../utils/deadheadEta';

export default function TempTripFeasibilityPanel({
  isOpen,
  onClose,
  assignmentData,
  userConfig = {},
}) {
  const [tempTrips, setTempTrips] = useState([]);
  const [feasibilityResult, setFeasibilityResult] = useState({ feasible: new Map(), rejected: new Map() });
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  
  const timezone = assignmentData?.metadata?.timezone || 'America/Los_Angeles';

  // Get current time in company timezone for datetime-local input
  const getCurrentCompanyTime = useCallback(() => {
    const now = new Date();
    // Convert to company timezone and format for datetime-local input
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;
    
    // Format: YYYY-MM-DDTHH:mm (for datetime-local input)
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }, [timezone]);
  
  // Trip creation form state
  const [tripForm, setTripForm] = useState(() => {
    const currentTime = getCurrentCompanyTime();
    return {
      clientName: '',
      pickupAddress: '',
      pickupLat: '',
      pickupLng: '',
      dropoffAddress: '',
      dropoffLat: '',
      dropoffLng: '',
      tripId: '',
      distance: '',
      scheduleTime: currentTime,
      appointmentTime: currentTime,
      los: '',
      spaceType: '',
    };
  });

  // Update times when panel opens or timezone changes
  useEffect(() => {
    if (isOpen) {
      const currentTime = getCurrentCompanyTime();
      setTripForm(prev => ({
        ...prev,
        scheduleTime: prev.scheduleTime || currentTime,
        appointmentTime: prev.appointmentTime || currentTime,
      }));
    }
  }, [isOpen, getCurrentCompanyTime]);

  const driversMap = useMemo(() => {
    const map = {};
    for (const d of assignmentData?.drivers || []) {
      const id = String(d.driver_id || d.id || '');
      if (id) map[id] = d;
    }
    return map;
  }, [assignmentData?.drivers]);

  // Convert datetime-local input to UTC ISO string
  const convertToUTC = useCallback((datetimeLocal, companyTimezone) => {
    if (!datetimeLocal) return '';
    
    // datetime-local format: "2026-02-27T16:00"
    // We interpret this as 4 PM on Feb 27 in the company timezone
    
    // Parse input
    const [datePart, timePart] = datetimeLocal.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    
    // Create an ISO string with the date/time and parse it
    // We'll create a date as if it's in the company timezone, then get UTC
    const isoString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
    
    // Use Date.toLocaleString to find what time in UTC gives us the desired company time
    // Binary search or iterate to find the right UTC time
    // Simpler: use the Intl API to get timezone offset
    
    // Create a date object from the string (treats it as local browser time)
    const localDate = new Date(isoString);
    
    // Get what this date looks like in the company timezone
    const companyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: companyTimezone,
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const companyParts = companyFormatter.formatToParts(localDate);
    const companyHour = parseInt(companyParts.find(p => p.type === 'hour').value);
    const companyMinute = parseInt(companyParts.find(p => p.type === 'minute').value);
    const companyDay = parseInt(companyParts.find(p => p.type === 'day').value);
    const companyMonth = parseInt(companyParts.find(p => p.type === 'month').value);
    const companyYear = parseInt(companyParts.find(p => p.type === 'year').value);
    
    // Calculate offset: difference between desired and actual
    const desiredMs = Date.UTC(year, month - 1, day, hour, minute);
    const actualMs = Date.UTC(companyYear, companyMonth - 1, companyDay, companyHour, companyMinute);
    const offsetMs = desiredMs - actualMs;
    
    // Apply offset to local date to get correct UTC
    const utcMs = localDate.getTime() + offsetMs;
    return new Date(utcMs).toISOString();
  }, []);

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setTripForm(prev => ({ ...prev, [field]: value }));
  };

  // Add trip to list
  const handleAddTrip = useCallback(() => {
    // Validate required fields
    if (!tripForm.clientName || !tripForm.scheduleTime || !tripForm.appointmentTime) {
      alert('Please fill in at least Client Name, Schedule Time, and Appointment Time');
      return;
    }

    // Convert times from company timezone to UTC
    const scheduleTimeUTC = convertToUTC(tripForm.scheduleTime, timezone);
    const appointmentTimeUTC = convertToUTC(tripForm.appointmentTime, timezone);

    // Calculate distance if not provided and coordinates are available
    let tripDistance = parseFloat(tripForm.distance) || 0;
    if (!tripDistance && tripForm.pickupLat && tripForm.pickupLng && tripForm.dropoffLat && tripForm.dropoffLng) {
      tripDistance = haversineDistance(
        parseFloat(tripForm.pickupLat),
        parseFloat(tripForm.pickupLng),
        parseFloat(tripForm.dropoffLat),
        parseFloat(tripForm.dropoffLng)
      );
      console.log('📏 Auto-calculated trip distance from coordinates:', tripDistance.toFixed(2), 'miles');
    }

    console.log('🕐 Timezone conversion:', {
      timezone,
      input_schedule: tripForm.scheduleTime,
      output_schedule_UTC: scheduleTimeUTC,
      input_appointment: tripForm.appointmentTime,
      output_appointment_UTC: appointmentTimeUTC
    });

    const newTrip = {
      _id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      priorityClient: { displayName: tripForm.clientName },
      authorizationNumber: '',
      jobOriginAddress: tripForm.pickupAddress,
      jobOriginLongitude: parseFloat(tripForm.pickupLng) || 0,
      jobOriginLatitude: parseFloat(tripForm.pickupLat) || 0,
      jobDestinationAddress: tripForm.dropoffAddress,
      jobDestinationLongitude: parseFloat(tripForm.dropoffLng) || 0,
      jobDestinationLatitude: parseFloat(tripForm.dropoffLat) || 0,
      tripId: tripForm.tripId || `TEMP-${Date.now()}`,
      milage: tripDistance,  // Use calculated or entered distance
      scheduleTime: scheduleTimeUTC,      // Store as UTC ISO string
      appointmentTime: appointmentTimeUTC, // Store as UTC ISO string
      isBreakTrip: false,
      los: tripForm.los,
      spaceType: tripForm.spaceType,
    };

    console.log('✅ Created trip:', newTrip);

    setTempTrips(prev => [...prev, newTrip]);
    
    // Clear form but keep current time for next trip
    handleClearForm();
  }, [tripForm, timezone, convertToUTC, getCurrentCompanyTime]);

  // Clear form manually
  const handleClearForm = useCallback(() => {
    const currentTime = getCurrentCompanyTime();
    setTripForm({
      clientName: '',
      pickupAddress: '',
      pickupLat: '',
      pickupLng: '',
      dropoffAddress: '',
      dropoffLat: '',
      dropoffLng: '',
      tripId: '',
      distance: '',
      scheduleTime: currentTime,
      appointmentTime: currentTime,
      los: '',
      spaceType: '',
    });
  }, [getCurrentCompanyTime]);

  // Remove trip from list
  const handleRemoveTrip = useCallback((tripId) => {
    setTempTrips(prev => prev.filter(t => t._id !== tripId));
  }, []);

  const handleCheckFeasibility = useCallback(() => {
    if (!assignmentData || !tempTrips?.length) return;
    setIsChecking(true);
    setFeasibilityResult({ feasible: new Map(), rejected: new Map() });
    setSelectedDriverId(null);
    setSelectedTripId(null);

    setTimeout(() => {
      try {
        const result = checkFeasibility(tempTrips, assignmentData, userConfig);
        setFeasibilityResult(result || { feasible: new Map(), rejected: new Map() });
      } catch (err) {
        console.error('[Feasibility] checkFeasibility failed:', err);
        setFeasibilityResult({ feasible: new Map(), rejected: new Map() });
      } finally {
        setIsChecking(false);
      }
    }, 0);
  }, [assignmentData, tempTrips, userConfig]);

  const selectedDriver = useMemo(() => {
    if (!selectedDriverId) return null;
    return driversMap[selectedDriverId] ?? null;
  }, [selectedDriverId, driversMap]);

  const tripsToShow = tempTrips.filter((t) => !(t.isBreakTrip ?? t.is_break));

  const buttonDisabled = !assignmentData || !tempTrips?.length || isChecking;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[3000]"
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
          role="button"
          tabIndex={0}
          aria-label="Close panel"
        />
      )}

      {/* Sliding panel — must sit above backdrop */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-6xl bg-white shadow-2xl z-[3010] transform transition-transform duration-300 ease-in-out flex flex-col border-l border-gray-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
        role="dialog"
        aria-label="Trip Feasibility"
      >
        {/* Header - Matching Branding */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between shadow-sm z-20">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-2 rounded-lg shadow-sm">
              <Zap className="h-5 w-5 text-white fill-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Trip Feasibility Engine</h2>
              <p className="text-xs text-gray-500 font-medium">Audit routing viability for potential trips</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-md transition-all text-gray-400 hover:text-gray-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 3-column content */}
        <div className="flex-1 flex min-h-0 overflow-hidden bg-gray-50/30">
          
          {/* Column 1: Trip Construction — form + list each scroll so short viewports still work */}
          <div className="w-[340px] flex-shrink-0 flex flex-col min-h-0 border-r border-gray-200 bg-white">
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Construction</h3>
              
              <div className="space-y-3">
                {/* Client Name */}
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">Client Name *</label>
                  <input
                    type="text"
                    value={tripForm.clientName}
                    onChange={(e) => handleFormChange('clientName', e.target.value)}
                    className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                    placeholder="Enter client name"
                  />
                </div>

                {/* Times Grid */}
                <div className="grid grid-cols-1 gap-3">
                   <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">
                      Schedule Time * <span className="text-[9px] text-gray-400 normal-case font-medium">({timezone})</span>
                    </label>
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={tripForm.scheduleTime}
                        onChange={(e) => handleFormChange('scheduleTime', e.target.value)}
                        className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                      />
                    </div>
                    <p className="text-[9px] text-gray-400 mt-1">Enter time in company timezone</p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">
                      Appointment Time * <span className="text-[9px] text-gray-400 normal-case font-medium">({timezone})</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={tripForm.appointmentTime}
                      onChange={(e) => handleFormChange('appointmentTime', e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                    />
                    <p className="text-[9px] text-gray-400 mt-1">Enter time in company timezone</p>
                  </div>
                </div>

                {/* Addresses */}
                <div className="space-y-3">
                   <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">Pickup Address</label>
                    <div className="relative">
                      <MapPin className="absolute left-2.5 top-2 w-3 h-3 text-green-500" />
                      <input
                        type="text"
                        value={tripForm.pickupAddress}
                        onChange={(e) => handleFormChange('pickupAddress', e.target.value)}
                        className="w-full pl-8 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                        placeholder="Pickup address..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                       <input
                        type="number"
                        step="any"
                        value={tripForm.pickupLat}
                        onChange={(e) => handleFormChange('pickupLat', e.target.value)}
                        className="w-full px-3 py-1 text-[10px] rounded border-gray-200"
                        placeholder="Pickup Lat"
                      />
                      <input
                        type="number"
                        step="any"
                        value={tripForm.pickupLng}
                        onChange={(e) => handleFormChange('pickupLng', e.target.value)}
                        className="w-full px-3 py-1 text-[10px] rounded border-gray-200"
                        placeholder="Pickup Lng"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">Dropoff Address</label>
                    <div className="relative">
                      <MapPin className="absolute left-2.5 top-2 w-3 h-3 text-red-500" />
                      <input
                        type="text"
                        value={tripForm.dropoffAddress}
                        onChange={(e) => handleFormChange('dropoffAddress', e.target.value)}
                        className="w-full pl-8 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                        placeholder="Dropoff address..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                       <input
                        type="number"
                        step="any"
                        value={tripForm.dropoffLat}
                        onChange={(e) => handleFormChange('dropoffLat', e.target.value)}
                        className="w-full px-3 py-1 text-[10px] rounded border-gray-200"
                        placeholder="Dropoff Lat"
                      />
                      <input
                        type="number"
                        step="any"
                        value={tripForm.dropoffLng}
                        onChange={(e) => handleFormChange('dropoffLng', e.target.value)}
                        className="w-full px-3 py-1 text-[10px] rounded border-gray-200"
                        placeholder="Dropoff Lng"
                      />
                    </div>
                  </div>
                </div>

                {/* Trip ID & Distance */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">Trip ID</label>
                    <input
                      type="text"
                      value={tripForm.tripId}
                      onChange={(e) => handleFormChange('tripId', e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                      placeholder="Auto"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">Distance (mi)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripForm.distance}
                      onChange={(e) => handleFormChange('distance', e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                      placeholder="Auto"
                    />
                    <p className="text-[8px] text-gray-400 mt-1 leading-tight">Leave empty to auto-calculate from coordinates</p>
                  </div>
                </div>

                {/* LOS & Space Type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">LOS ID</label>
                    <input
                      type="text"
                      value={tripForm.los}
                      onChange={(e) => handleFormChange('los', e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">Space Type</label>
                    <input
                      type="text"
                      value={tripForm.spaceType}
                      onChange={(e) => handleFormChange('spaceType', e.target.value)}
                      className="w-full px-3 py-1.5 text-xs rounded-md border-gray-200 focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                    />
                  </div>
                </div>

                <div className="pt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={handleClearForm}
                    className="w-full py-2 bg-white text-gray-700 border border-gray-200 rounded-md text-[11px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleAddTrip}
                    className="w-full py-2 bg-main-primary text-white rounded-md text-[11px] font-black uppercase tracking-widest shadow-sm hover:bg-main-primary/90 transition-all flex items-center justify-center gap-2"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>

            {/* Created Trips List */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3 bg-gray-50/50">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Constructed ({tempTrips.length})</h3>
              </div>
              {tripsToShow.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center opacity-40">
                   <div className="bg-gray-100 p-3 rounded-full mb-2">
                     <Zap className="w-6 h-6 text-gray-400" />
                   </div>
                   <p className="text-[10px] font-bold text-gray-500 uppercase">Input requirements above</p>
                </div>
              ) : (
                tripsToShow.map((trip) => (
                  <div key={trip._id || trip.id} className="relative group">
                    <TempTripCard
                      trip={trip}
                      timezone={timezone}
                    />
                    <button
                      onClick={() => handleRemoveTrip(trip._id || trip.id)}
                      className="absolute -top-1 -right-1 p-1 rounded-full bg-white shadow-md border border-gray-100 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100"
                      title="Remove trip"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Check Feasibility Button */}
            <div className="flex-shrink-0 p-4 border-t border-gray-100 bg-white">
              <button
                type="button"
                onClick={handleCheckFeasibility}
                disabled={buttonDisabled}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-md text-xs font-black uppercase tracking-widest shadow-lg hover:shadow-indigo-500/20 hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isChecking ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking Logic…
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 fill-white" />
                    Verify Feasibility
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Column 2: Potential Matches */}
          <div className="w-[280px] flex-shrink-0 flex flex-col min-h-0 border-r border-gray-200 bg-white">
            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Potential Drivers</h3>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
              {tripsToShow.length === 0 ? (
                <div className="text-[10px] font-bold text-gray-400 uppercase italic opacity-50 py-12 text-center">Construct trips to audit matches</div>
              ) : (
                <div className="space-y-6">
                  {tripsToShow.map((trip) => {
                    const tripId = trip._id || trip.id;
                    const driverIds = feasibilityResult?.feasible?.get(tripId) || [];
                    const rejectedReasons = feasibilityResult?.rejected?.get(tripId) || new Map();
                    return (
                      <div key={tripId} className="space-y-2 pb-4 border-b border-gray-50 last:border-0">
                        <div className="text-[10px] font-black text-gray-900 uppercase tracking-tight truncate">
                          {trip.priorityClient?.displayName ?? trip.client_name ?? tripId}
                        </div>
                        <PossibleDriversList
                          tripId={tripId}
                          driverIds={driverIds}
                          rejectedReasons={rejectedReasons}
                          driversMap={driversMap}
                          selectedDriverId={selectedDriverId}
                          selectedTripId={selectedTripId}
                          onSelectDriver={(driverId) => {
                            setSelectedDriverId(driverId);
                            setSelectedTripId(tripId);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Column 3: Audit Detail */}
          <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-white">
            <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Feasibility Insights</h3>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              <DriverDetailView
                driver={selectedDriver}
                timezone={timezone}
                feasibilityResult={feasibilityResult}
                tempTrips={tripsToShow}
                selectedDriverId={selectedDriverId}
                selectedTripId={selectedTripId}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
